import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import InstallPrompt from './components/InstallPrompt';
import SentinelAvatar from './components/sentinel/SentinelAvatar';
import './App.css';

// Silence the harmless ResizeObserver loop notification. Radix + shadcn
// components (Popover, Select, Command) trip it constantly; CRA's dev overlay
// treats it as a runtime error which panics candidates during the wizard.
// See: https://github.com/WICG/resize-observer/issues/38
if (typeof window !== 'undefined') {
  const RESIZE_OBSERVER_MSGS = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
  ];
  window.addEventListener('error', (e) => {
    if (RESIZE_OBSERVER_MSGS.some((m) => e.message && e.message.includes(m))) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
  // Suppress the console.error variant CRA's overlay listens to.
  const origErr = window.console.error;
  window.console.error = (...args) => {
    const msg = args[0];
    if (typeof msg === 'string' && RESIZE_OBSERVER_MSGS.some((m) => msg.includes(m))) return;
    return origErr.apply(window.console, args);
  };
}

// Eager imports for the two public routes an unauthenticated visitor lands on.
// Everything else is code-split so the landing bundle stays lean.
import Landing from './pages/Landing';
import Login from './pages/Login';

// Lazy-loaded routes — each becomes its own chunk fetched on demand.
const CandidateRegister = lazy(() => import('./pages/CandidateRegister'));
import { FEATURES } from './config/features';
const SectorPage = lazy(() => import('./pages/SectorPage'));
const VerifyBuddyBot = lazy(() => import('./pages/bot/VerifyBuddyBot'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const Companies = lazy(() => import('./pages/admin/Companies'));
const AdminQcQueue = lazy(() => import('./pages/admin/AdminQcQueue'));
const ArgusWarRoom = lazy(() => import('./pages/argus/ArgusWarRoom'));
const CompanyDashboard = lazy(() => import('./pages/company/Dashboard'));
const CreateRequest = lazy(() => import('./pages/company/CreateRequest'));
const CandidatePortal = lazy(() => import('./pages/candidate/Wizard'));
const CandidateSubmissions = lazy(() => import('./pages/candidate/Submissions'));
const AgentDashboard = lazy(() => import('./pages/agent/Dashboard'));
const SubmitVerification = lazy(() => import('./pages/agent/SubmitVerification'));
const AgentCaseView = lazy(() => import('./pages/agent/CaseView'));
const FieldAgents = lazy(() => import('./pages/admin/FieldAgents'));
const BGVRequests = lazy(() => import('./pages/admin/BGVRequests'));
const CaseDetail = lazy(() => import('./pages/admin/CaseDetail'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const SettingsPage = lazy(() => import('./pages/admin/Settings'));
const AuditLogs = lazy(() => import('./pages/admin/AuditLogs'));
const WorkflowPackages = lazy(() => import('./pages/admin/WorkflowPackages'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const RolesAdmin = lazy(() => import('./pages/admin/Roles'));
const ConnectorAdmin = lazy(() => import('./pages/admin/Connector'));
const ReportBuilder = lazy(() => import('./pages/admin/ReportBuilder'));
const AgentActivity = lazy(() => import('./pages/admin/AgentActivity'));
const TopologyPreview = lazy(() => import('./pages/admin/TopologyPreview'));
const Rules = lazy(() => import('./pages/admin/Rules'));
const BotConsole = lazy(() => import('./pages/admin/BotConsole'));
const HrmsStoryPage = lazy(() => import('./pages/admin/HrmsStoryPage'));
const ProvidersConsole = lazy(() => import('./pages/admin/ProvidersConsole'));
const BulkUpload = lazy(() => import('./pages/company/BulkUpload'));
const EmailIntake = lazy(() => import('./pages/company/EmailIntake'));
const Help = lazy(() => import('./pages/Help'));
const RecoverAdmin = lazy(() => import('./pages/RecoverAdmin'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const VerifyReport = lazy(() => import('./pages/VerifyReport'));

// Manpower Agency module — slim dashboard, bulk upload, live check, billing.
// Only reachable when the logged-in company user's tenant has
// `company_category === 'manpower_agency'` (enforced by backend guard as well).
const AgencyLayout = lazy(() => import('./pages/agency/AgencyLayout'));
const AgencyDashboard = lazy(() => import('./pages/agency/AgencyDashboard'));
const AgencyBulkUpload = lazy(() => import('./pages/agency/AgencyBulkUpload'));
const AgencyLiveCheck = lazy(() => import('./pages/agency/AgencyLiveCheck'));
const AgencyKYB = lazy(() => import('./pages/agency/AgencyKYB'));
const AgencyBilling = lazy(() => import('./pages/agency/AgencyBilling'));
const AgencyCaseDetail = lazy(() => import('./pages/agency/AgencyCaseDetail'));
const DemoDesk = lazy(() => import('./pages/demo/DemoDesk'));
const CompleteCheck = lazy(() => import('./pages/demo/CompleteCheck'));
const AgencySettings = lazy(() => import('./pages/agency/AgencySettings'));
const AgencyHub = lazy(() => import('./pages/admin/agency_hub/AgencyHub'));
const HubAgencyDetail = lazy(() => import('./pages/admin/agency_hub/HubAgencyDetail'));
// Corporate Sector — rendered when company_category === 'corporate'.
const CorporateLayout = lazy(() => import('./pages/corporate/CorporateLayout'));
const CorporateDashboard = lazy(() => import('./pages/corporate/CorporateDashboard'));
const CorporateInvite = lazy(() => import('./pages/corporate/CorporateInvite'));
const CorporateBulkInvite = lazy(() => import('./pages/corporate/CorporateBulkInvite'));
const CorporateCases = lazy(() => import('./pages/corporate/CorporateCases'));

// Dashboard Studio (custom dashboards) — shared by admin + company/HR + staff
const DashboardStudioHome = lazy(() => import('./pages/DashboardStudio').then((m) => ({ default: m.DashboardStudioHome })));
const DashboardStudioView = lazy(() => import('./pages/DashboardStudio').then((m) => ({ default: m.DashboardStudioView })));

// Shared spinner shown while an auth-check or lazy chunk is loading.
const FullPageLoader = ({ label = 'Loading...' }) => (
  <div className="min-h-screen flex items-center justify-center" data-testid="page-loader">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
      <p className="mt-4 text-gray-600">{label}</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, requiredPerm }) => {
  const { user, loading, hasPerm } = useAuth();

  if (loading) return <FullPageLoader />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If a specific permission is required, allow staff who have that perm
  // even if their raw role is 'staff' (not in the allowedRoles list).
  if (requiredPerm && hasPerm(requiredPerm)) {
    return children;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Staff users on /admin/* routes — let them in if they have ANY
    // permission at all (nav visibility further gates the sub-actions).
    if (
      user.role === 'staff'
      && allowedRoles.includes('admin')
      && (!requiredPerm)  // no explicit perm gate — fall back to nav gating
    ) {
      return children;
    }
    // Redirect to appropriate dashboard based on role
    if (user.role === 'staff' && (user.role_key === 'demo_sales' || user.role_id === 'demo_sales')) {
      return <Navigate to="/demo/banking" replace />;
    }
    if (user.role === 'admin' || user.role === 'staff') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.role === 'company') {
      if (user.company_category === 'manpower_agency') return <Navigate to="/agency/dashboard" replace />;
      if (user.company_category === 'corporate') return <Navigate to="/corporate/dashboard" replace />;
      return <Navigate to="/company/dashboard" replace />;
    } else if (user.role === 'candidate') {
      return <Navigate to="/candidate/portal" replace />;
    } else if (user.role === 'field_agent') {
      return <Navigate to="/agent/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <FullPageLoader />;

  if (user) {
    // Redirect to appropriate dashboard
    if (user.role === 'staff' && (user.role_key === 'demo_sales' || user.role_id === 'demo_sales')) {
      return <Navigate to="/demo/banking" replace />;
    }
    if (user.role === 'admin' || user.role === 'staff') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.role === 'company') {
      if (user.company_category === 'manpower_agency') return <Navigate to="/agency/dashboard" replace />;
      if (user.company_category === 'corporate') return <Navigate to="/corporate/dashboard" replace />;
      return <Navigate to="/company/dashboard" replace />;
    } else if (user.role === 'candidate') {
      return <Navigate to="/candidate/portal" replace />;
    } else if (user.role === 'field_agent') {
      return <Navigate to="/agent/dashboard" replace />;
    }
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <Suspense fallback={<FullPageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
              <Route path="/sectors/:slug" element={<SectorPage />} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/verify" element={<CandidateRegister />} />
              {FEATURES.bot && <Route path="/bot" element={<VerifyBuddyBot />} />}
              <Route path="/verify-report/:reportId" element={<VerifyReport />} />
              <Route path="/recover-admin" element={<RecoverAdmin />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />

              {/* Admin Routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/companies" element={<ProtectedRoute allowedRoles={['admin']}><Companies /></ProtectedRoute>} />
              <Route path="/admin/qc-queue" element={<ProtectedRoute allowedRoles={['admin']}><AdminQcQueue /></ProtectedRoute>} />
              <Route path="/argus" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'company']}><ArgusWarRoom /></ProtectedRoute>} />
              <Route path="/admin/field-agents" element={<ProtectedRoute allowedRoles={['admin']}><FieldAgents /></ProtectedRoute>} />
              <Route path="/admin/bgv-requests" element={<ProtectedRoute allowedRoles={['admin']}><BGVRequests /></ProtectedRoute>} />
              <Route path="/admin/cases/:caseRef" element={<ProtectedRoute allowedRoles={['admin', 'field_agent']}><CaseDetail /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['admin']}><Analytics /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['admin']}><SettingsPage /></ProtectedRoute>} />
              <Route path="/admin/audit-logs" element={<ProtectedRoute allowedRoles={['admin']}><AuditLogs /></ProtectedRoute>} />
              <Route path="/admin/workflow-packages" element={<ProtectedRoute allowedRoles={['admin']}><WorkflowPackages /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/admin/roles" element={<ProtectedRoute allowedRoles={['admin']}><RolesAdmin /></ProtectedRoute>} />
              {FEATURES.hrmsConnector && <Route path="/admin/connector" element={<ProtectedRoute allowedRoles={['admin']}><ConnectorAdmin /></ProtectedRoute>} />}
              <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['admin']}><ReportBuilder /></ProtectedRoute>} />
              <Route path="/admin/field-agents/:agentId/activity" element={<ProtectedRoute allowedRoles={['admin']}><AgentActivity /></ProtectedRoute>} />
              {FEATURES.topologyPreview && <Route path="/admin/topology-preview" element={<ProtectedRoute allowedRoles={['admin']}><TopologyPreview /></ProtectedRoute>} />}
              <Route path="/admin/rules" element={<ProtectedRoute allowedRoles={['admin']}><Rules /></ProtectedRoute>} />
              {FEATURES.bot && <Route path="/admin/bot-console" element={<ProtectedRoute allowedRoles={['admin','company','staff']}><BotConsole /></ProtectedRoute>} />}
              {FEATURES.hrmsStory && <Route path="/admin/hrms-story" element={<ProtectedRoute allowedRoles={['admin','company','staff']}><HrmsStoryPage /></ProtectedRoute>} />}
              <Route path="/admin/providers" element={<ProtectedRoute allowedRoles={['admin']}><ProvidersConsole /></ProtectedRoute>} />

              {/* Dashboard Studio — hidden in Phase 1 (FEATURES.dashboardStudio) */}
              {FEATURES.dashboardStudio && <Route path="/dashboards" element={<ProtectedRoute allowedRoles={['admin','company','staff']}><DashboardStudioHome /></ProtectedRoute>} />}
              {FEATURES.dashboardStudio && <Route path="/dashboards/:dashboardId" element={<ProtectedRoute allowedRoles={['admin','company','staff']}><DashboardStudioView /></ProtectedRoute>} />}

              {/* Company Routes */}
              <Route path="/company/dashboard" element={<ProtectedRoute allowedRoles={['company']}><CompanyDashboard /></ProtectedRoute>} />
              <Route path="/company/create-request" element={<ProtectedRoute allowedRoles={['company', 'admin']}><CreateRequest /></ProtectedRoute>} />
              <Route path="/company/bulk-upload" element={<ProtectedRoute allowedRoles={['company']}><BulkUpload /></ProtectedRoute>} />
              <Route path="/company/email-intake" element={<ProtectedRoute allowedRoles={['company']}><EmailIntake /></ProtectedRoute>} />

              {/* Help - accessible to any logged-in user */}
              <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />

              {/* Candidate Routes */}
              <Route path="/candidate/portal" element={<ProtectedRoute allowedRoles={['candidate']}><CandidatePortal /></ProtectedRoute>} />
              <Route path="/candidate/submissions" element={<ProtectedRoute allowedRoles={['candidate']}><CandidateSubmissions /></ProtectedRoute>} />

              {/* Field Agent Routes */}
              <Route path="/agent/dashboard" element={<ProtectedRoute allowedRoles={['field_agent']}><AgentDashboard /></ProtectedRoute>} />
              <Route path="/agent/submit/:assignmentId" element={<ProtectedRoute allowedRoles={['field_agent']}><SubmitVerification /></ProtectedRoute>} />
              <Route path="/agent/cases/:caseRef" element={<ProtectedRoute allowedRoles={['field_agent']}><AgentCaseView /></ProtectedRoute>} />

              {/* Manpower Agency Routes — nested under a shared layout */}
              <Route path="/demo" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><DemoDesk sector="agency" /></ProtectedRoute>} />
              <Route path="/demo/banking" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><DemoDesk sector="banking" /></ProtectedRoute>} />
              <Route path="/demo/complete-check" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><CompleteCheck /></ProtectedRoute>} />
              <Route path="/agency" element={<ProtectedRoute allowedRoles={['company', 'admin']}><AgencyLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/agency/dashboard" replace />} />
                <Route path="dashboard" element={<AgencyDashboard />} />
                <Route path="upload" element={<AgencyBulkUpload />} />
                <Route path="live-check" element={<AgencyLiveCheck />} />
                <Route path="kyb" element={<AgencyKYB />} />
                <Route path="billing" element={<AgencyBilling />} />
                <Route path="settings" element={<AgencySettings />} />
                <Route path="cases/:bgv_id" element={<AgencyCaseDetail />} />
              </Route>

              {/* Agency Hub — admin control centre for agency verticals/tenants */}
              <Route path="/admin/agency-hub" element={<ProtectedRoute allowedRoles={['admin']} requiredPerm="companies.view"><AgencyHub /></ProtectedRoute>} />
              <Route path="/admin/agency-hub/agencies/:cid" element={<ProtectedRoute allowedRoles={['admin']} requiredPerm="companies.view"><HubAgencyDetail /></ProtectedRoute>} />

              {/* Corporate Sector Routes — nested under a shared layout */}
              <Route path="/corporate" element={<ProtectedRoute allowedRoles={['company']}><CorporateLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/corporate/dashboard" replace />} />
                <Route path="dashboard" element={<CorporateDashboard />} />
                <Route path="invite" element={<CorporateInvite />} />
                <Route path="bulk-invite" element={<CorporateBulkInvite />} />
                <Route path="cases" element={<CorporateCases />} />
              </Route>

              {/* 404 Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          {/* PWA install prompt — self-suppresses when already installed,
              when dismissed within the last 7 days, or when the current
              user's role doesn't warrant nagging (see AgentInstallGate). */}
          <AgentInstallGate />
          {/* VerifyPro Sentinel — ambient AI avatar. Renders only for users
              the admin has explicitly granted access (`user.sentinel_enabled`). */}
          <SentinelAvatar />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Renders <InstallPrompt/> only for field agents (they're the primary mobile
 * audience). Admins on desktop and candidates on one-off wizard flows don't
 * benefit from installing the app, so we keep their UI clean.
 */
function AgentInstallGate() {
  const { user } = useAuth();
  if (user?.role !== 'field_agent') return null;
  return <InstallPrompt />;
}

export default App;
